import type { SimEvent } from "../protocol/events";
import type { GamePhase } from "../protocol/snapshot";
import { applyUnplayedHazards } from "./cards";
import type { CardDef } from "./definitions";
import { heldPenalties } from "./hazardCards";
import type { SetupResult } from "./setup";
import { drawHand, facilityMitigationOf, spendFacilityMitigation, type EngineState } from "./state";
import { failForQuotaMissed, runTick } from "./tick";

/**
 * The phase machine from `00-pillars.md` §3: Setup (once, before turn 1) ->
 * Draw -> Play -> Run -> Status -> Draw -> ... `setup` is never re-entered
 * (spec 0017) — every stage opens there and leaves it exactly once, into the
 * Phase 2 loop the rest of this file predates.
 *
 * `advancePhase` is the only way to move between phases, so every transition
 * runs its entry work exactly once (dealing a hand, resetting the power
 * budget, firing unplayed hazards, checking the turn budget).
 *
 * Refusable in exactly one case (spec 0027): leaving `setup` while any
 * sourced `inputTank` has no chosen contract. Every other transition still
 * always succeeds and mutates exactly as before.
 */

const NEXT_PHASE: Record<GamePhase, GamePhase> = {
  setup: "draw",
  draw: "play",
  play: "run",
  run: "status",
  status: "draw",
};

export function advancePhase(
  state: EngineState,
  cards: Record<string, CardDef>,
  events: SimEvent[],
): SetupResult {
  if (state.isRunComplete || state.isRunFailed) return { ok: true };

  const from = state.phase;
  const to = NEXT_PHASE[from];

  // Setup cannot end unchosen (spec 0027, Constraints): checked before any
  // mutation, so a refusal here leaves state and events untouched. Sorted so
  // the named tank is the first in stable id order, not discovery order.
  if (from === "setup") {
    const sortedNodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const unchosen = sortedNodes.find((n) => n.sourcingOptions !== null && n.precursor === null);
    if (unchosen) {
      return {
        ok: false,
        reason: `${unchosen.id} has no chosen supply contract`,
        code: "sourcingUnchosen",
        sourceId: unchosen.id,
      };
    }
  }

  // Leaving Play: any hazard card still in hand does its damage.
  if (from === "play") {
    applyUnplayedHazards(state, cards, events);
  }

  state.phase = to;
  events.push({ type: "phase.changed", from, to });

  switch (to) {
    case "draw":
      state.turn += 1;
      // Low Fire lasts "until the next draw" (spec 0035).
      for (const node of state.nodes) node.maxMultipleThisTurn = null;
      drawHand(state, cards);
      break;

    case "play": {
      // Budget resets at the start of each Play phase (`02-cards-and-economy.md`
      // §1's Power Budget) — the
      // ceiling as well as the spend, so an Overtime Shift buys kW for one
      // turn rather than permanently raising the rig's supply.
      state.energyBudgetKw = state.stage.energyBudgetKw;
      // Spec 0028 (G-D9): captured before the reset below consumes it, so
      // Emergency Power Cell can tell "the blackout this entry is spending"
      // from one already paid off on an earlier entry to Play.
      const wasBlackoutPending = state.isBlackoutPending;
      // 03-hazards.md §3.2 (spec 0006, Open Question 2): a pending scripted
      // blackout zeroes available kW for this Play phase only — spending it
      // straight up to the reset ceiling rather than a second budget field.
      state.energySpentKw = state.isBlackoutPending ? state.energyBudgetKw : 0;
      state.isBlackoutPending = false;
      // Spec 0067: the spill-free moves are per turn, so each Play entry refills them.
      state.spillFreeMovesUsedThisTurn = 0;

      // Spec 0007, open questions 7 and 10: held kW penalties are read once,
      // on entry to Play, and are not retroactive to a card drawn mid-turn.
      const held = heldPenalties(state, cards);
      if (held.powerPenaltyKw > 0 || held.powerDrain) {
        state.energyBudgetKw = Math.max(state.energySpentKw, state.energyBudgetKw - held.powerPenaltyKw);
        if (held.powerDrain) state.energyBudgetKw = state.energySpentKw;

        for (const instance of state.hand) {
          const card = cards[instance.cardId];
          if (!card || card.category !== "hazard" || !card.hazard) continue;
          if (card.hazard.kind === "powerPenalty") {
            events.push({
              type: "hazard.cardPenalty",
              cardId: instance.cardId,
              kind: "powerPenalty",
              amount: card.hazard.kw,
              targetId: null,
            });
          } else if (card.hazard.kind === "powerDrain") {
            events.push({ type: "hazard.cardPenalty", cardId: instance.cardId, kind: "powerDrain", amount: 0, targetId: null });
          }
        }
      }

      // Spec 0028 (G-D9): the ordering's "additions" step — run after the
      // penalty reduction above, per energyBudgetKw's own doc comment
      // (state.ts). Emergency Power Cell only ever finds work on the first
      // turn that actually zeroed available kW this stage: a scripted
      // blackout entering this Play phase, or a held Power Grid Outage card.
      const emergencyPower = facilityMitigationOf(state, "emergencyPower");
      const emergencyPowerSpent = state.installedFacilityMitigations.some(
        (m) => m.mitigation.kind === "emergencyPower" && m.spent,
      );
      if (emergencyPower && !emergencyPowerSpent && (wasBlackoutPending || held.powerDrain)) {
        state.energyBudgetKw += emergencyPower.kw;
        spendFacilityMitigation(state, "emergencyPower");
      }
      break;
    }

    case "run":
      state.tickInTurn = 0;
      // Spec 0033 §3.3: resets `heldPacketThisTurn` for the new turn AND
      // captures the entry-state checkpoint in the same write — a node
      // holding a packet right now has already earned this turn's keep.
      for (const node of state.nodes) {
        if (node.infestation === null) continue;
        node.infestation.heldPacketThisTurn = node.buffer.length >= 1;
      }
      break;

    case "status": {
      // Spec 0033 §3.3 (decision 4, operational reading): starved out once a
      // turn other than the infestation's own turn passes with the node never
      // holding a packet — checked here, sorted by id, before the quota check
      // below (order between the two doesn't matter; both are independent).
      const infested = [...state.nodes]
        .filter((n) => n.infestation !== null)
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      for (const node of infested) {
        const infestation = node.infestation!;
        if (state.turn > infestation.sinceTurn && !infestation.heldPacketThisTurn) {
          node.infestation = null;
          events.push({ type: "node.infestationCleared", nodeId: node.id, cause: "starved" });
        }
      }

      // Quota must be met inside the turn budget.
      if (!state.isRunComplete && state.turn >= state.stage.maxTurns) {
        failForQuotaMissed(state, events);
      }
      break;
    }
  }

  return { ok: true };
}

/** Runs the whole Run phase: ticksPerTurn ticks, or fewer if the run ends. */
export function runPhaseTicks(state: EngineState, events: SimEvent[]): void {
  while (state.tickInTurn < state.stage.ticksPerTurn) {
    if (state.isRunComplete || state.isRunFailed) return;
    events.push(...runTick(state));
    state.tickInTurn += 1;
  }
}

/** One tick of the Run phase, for step-through debugging and the UI's tick clock. */
export function runSingleTick(state: EngineState, events: SimEvent[]): boolean {
  if (state.phase !== "run") return false;
  if (state.isRunComplete || state.isRunFailed) return false;
  if (state.tickInTurn >= state.stage.ticksPerTurn) return false;

  events.push(...runTick(state));
  state.tickInTurn += 1;
  return true;
}

/** Re-exported so the phase machine still reads as the owner of the deal. */
export { drawHand } from "./state";
